import { useDeleteConfirmation, useServices } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { useCrudNotifications, usePaginatedList } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { AccessDevice, DeviceStatus, DeviceType } from '@/shared/types';
import { Camera, Car, Edit2, Plus, RefreshCw, Save, Server, Trash2, User, Wifi, WifiOff, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

export const AdminDeviceList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const { confirmDelete } = useDeleteConfirmation();
  const { deviceRepo } = useServices();

  if (!ability.can('manage', 'all') && !ability.can('manage', 'AccessDevice') && !ability.can('read', 'AccessDevice')) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        No tienes permisos para ver esta sección.
      </div>
    );
  }

  const canManageAccessDevice = ability.can('manage', 'all') || ability.can('manage', 'AccessDevice');
  const canCreateAccessDevice = canManageAccessDevice || ability.can('create', 'AccessDevice');
  const canUpdateAccessDevice = canManageAccessDevice || ability.can('update', 'AccessDevice');
  const canDeleteAccessDevice = canManageAccessDevice || ability.can('delete', 'AccessDevice');
  const canSyncAccessDevices = canCreateAccessDevice || canUpdateAccessDevice;
  const [syncing, setSyncing] = useState(false);
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Dispositivo',
  });

  const {
    data: devices,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh,
  } = usePaginatedList({
    fetchFn: (params) => deviceRepo.getDevices(params),
    errorTitle: 'Error al cargar dispositivos',
  });

  const hasSyncedDevices = devices.some((d) => d.externalDeviceId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Partial<AccessDevice>>({});
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSync = async () => {
    if (!canSyncAccessDevices) return;
    setSyncing(true);
    try {
      const result = await deviceRepo.syncDevices();
      refresh();
      // Mostrar resultado brevemente — usamos console.log aquí, el toast lo maneja el repo internamente
      console.info(`Sync completado: +${result.created} creados, ${result.updated} actualizados, ${result.deactivated} desactivados`);
    } catch (err) {
      console.error('Error al sincronizar dispositivos', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenCreate = () => {
    if (!canCreateAccessDevice) return;
    setEditingDevice({
      type: DeviceType.VEHICLE_BARRIER,
      status: DeviceStatus.OFFLINE,
      ipAddress: '192.168.1.',
      location: ''
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (device: AccessDevice) => {
    if (!canUpdateAccessDevice) return;
    setEditingDevice({ ...device });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canDeleteAccessDevice) return;
    await confirmDelete({
      itemName: name,
      itemType: 'Dispositivo',
      onConfirm: async () => {
        await withDeleteNotification(() => deviceRepo.deleteDevice(id));
        refresh();
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDevice.name || !editingDevice.ipAddress) return;
    if (isEditMode && !canUpdateAccessDevice) return;
    if (!isEditMode && !canCreateAccessDevice) return;

    try {
      if (isEditMode && editingDevice.id) {
        await withUpdateNotification(() => deviceRepo.updateDevice(editingDevice as AccessDevice));
      } else {
        await withCreateNotification(() => deviceRepo.createDevice(editingDevice as Omit<AccessDevice, 'id'>));
      }
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const getTypeIcon = (type: DeviceType) => {
    switch (type) {
      case DeviceType.VEHICLE_BARRIER: return <Car size={16} />;
      case DeviceType.PEDESTRIAN_GATE: return <User size={16} />;
      case DeviceType.CAMERA_LPR: return <Camera size={16} />;
      case DeviceType.CONTROLLER: return <Server size={16} />;
      default: return <Server size={16} />;
    }
  };

  const columns: ColumnDef<AccessDevice>[] = useMemo(() => [
    {
      id: 'name',
      header: t('admin.devices.table.name'),
      cell: (device) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
            {getTypeIcon(device.type)}
          </div>
          <div>
            <p className="font-medium text-slate-800">{device.name}</p>
            <p className="text-xs text-slate-500">{device.location === "Controladora" ? device.name : device.location}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: t('admin.devices.table.type'),
      cell: (device) => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
          {t(`admin.devices.type.${device.type.toLowerCase().split('_')[0]}`)}
        </span>
      ),
    },
    // {
    //   id: 'ip',
    //   header: t('admin.devices.table.ip'),
    //   cell: (device) => (
    //     <span className="font-mono text-slate-600">{device.ipAddress}</span>
    //   ),
    // },
    {
      id: 'status',
      header: t('admin.devices.table.status'),
      cell: (device) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
          device.status === DeviceStatus.ONLINE
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {device.status === DeviceStatus.ONLINE ? <Wifi size={12}/> : <WifiOff size={12}/>}
          {t(`admin.devices.status.${device.status.toLowerCase()}`)}
        </span>
      ),
    },
  ], [t]);

  return (
    <>
      <DataTable
        data={devices}
        columns={columns}
        keyExtractor={(device) => device.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.devices.title')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar dispositivo..."
        loading={loading}
        emptyMessage="No hay dispositivos registrados."
        actions={canSyncAccessDevices || canCreateAccessDevice ? (
          <div className="flex gap-2">
            {canSyncAccessDevices && (
              <Button
                onClick={handleSync}
                disabled={syncing}
                variant="secondary"
                icon={<RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />}
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
            )}
            {!hasSyncedDevices && canCreateAccessDevice && (
              <Button onClick={handleOpenCreate} icon={<Plus size={16} />}>
                {t('admin.devices.create')}
              </Button>
            )}
          </div>
        ) : undefined}
        rowActions={(canUpdateAccessDevice || canDeleteAccessDevice) ? (device) => (
          device.externalDeviceId ? null : (
            <div className="flex justify-end gap-2">
              {canUpdateAccessDevice && (
                <button onClick={() => handleOpenEdit(device)} className="p-1.5 text-slate-400 hover:text-primary-600 rounded">
                  <Edit2 size={16} />
                </button>
              )}
              {canDeleteAccessDevice && (
                <button onClick={() => handleDelete(device.id, device.name)} className="p-1.5 text-slate-400 hover:text-red-600 rounded">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          )
        ) : undefined}
      />

      {isModalOpen && ((isEditMode && canUpdateAccessDevice) || (!isEditMode && canCreateAccessDevice)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{isEditMode ? t('common.edit') : t('admin.devices.create')}</h3>
              <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <Input
                label={t('admin.devices.table.name')}
                value={editingDevice.name || ''}
                onChange={e => setEditingDevice({...editingDevice, name: e.target.value})}
                required
              />

              <Input
                label="Ubicación"
                placeholder="Ej. Acceso Norte, Lobby A"
                value={editingDevice.location || ''}
                onChange={e => setEditingDevice({...editingDevice, location: e.target.value})}
                required
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.devices.table.type')}</label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                  value={editingDevice.type}
                  onChange={e => setEditingDevice({...editingDevice, type: e.target.value as DeviceType})}
                >
                  <option value={DeviceType.VEHICLE_BARRIER}>{t('admin.devices.type.vehicle')}</option>
                  <option value={DeviceType.PEDESTRIAN_GATE}>{t('admin.devices.type.pedestrian')}</option>
                  <option value={DeviceType.CAMERA_LPR}>{t('admin.devices.type.camera')}</option>
                  <option value={DeviceType.CONTROLLER}>{t('admin.devices.type.controller')}</option>
                </select>
              </div>

              <Input
                label={t('admin.devices.table.ip')}
                value={editingDevice.ipAddress || ''}
                onChange={e => setEditingDevice({...editingDevice, ipAddress: e.target.value})}
                placeholder="192.168.1.X"
                required
              />

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
